import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { QUICK_ACTIONS, type QuickAction } from "~/shared/data/chat-actions";
import { cn } from "~/shared/lib/utils";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";

export function EmptyState() {
    const { t } = useTranslation("chat");
    const applyPreset = useChatSettingsStore((s) => s.applyPreset);

    // Quick-action подменяет ТОЛЬКО тип артефакта и режим. Модель,
    // aspect ratio, resolution, duration, audio, batch — остаются такими,
    // какими их видит пользователь (стабильные дефолты из INITIAL или
    // то, что он сам уже выбрал в правой панели). Раньше сюда подсасывался
    // buildCheapestSettings, и клик «Text to Image» переключал модель с
    // дефолтного nano-banana-pro на дешёвый nano-banana-2 + aspect на 1:1
    // — пользователь это справедливо считал регрессом.
    const handleAction = useCallback(
        (action: QuickAction) => {
            applyPreset({
                artifactType: action.type,
                mode: action.mode,
            });
        },
        [applyPreset],
    );

    return (
        <div className="flex min-h-full flex-col items-center justify-center px-4 py-10 md:py-16">
            <div className="w-full max-w-3xl space-y-10 md:space-y-12">
                <div className="space-y-5 text-center">
                    <h2 className="min-h-[2.25rem] text-3xl font-semibold tracking-tight text-zinc-50 md:min-h-[2.75rem] md:text-4xl">
                        {t("empty.title")}
                    </h2>
                    <p className="mx-auto min-h-[4.875rem] max-w-xl text-base leading-relaxed text-zinc-400">
                        {t("empty.subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {QUICK_ACTIONS.map((action) => (
                        <QuickActionCard key={action.id} action={action} onClick={handleAction} />
                    ))}
                </div>
            </div>
        </div>
    );
}

const QuickActionCard = memo(function QuickActionCard({
    action,
    onClick,
    disabled,
}: {
    action: QuickAction;
    onClick: (action: QuickAction) => void;
    disabled?: boolean;
}) {
    const { t } = useTranslation("chat");
    const handleClick = useCallback(() => onClick(action), [onClick, action]);
    const Icon = action.icon;

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={cn(
                "group relative flex flex-col items-start gap-3 rounded-[1rem] border border-white/5 p-4 md:p-5",
                "[touch-action:manipulation] bg-gradient-to-br text-left",
                "transition-[background-color,border-color] duration-150 hover:border-white/10 hover:bg-white/5",
                "disabled:cursor-not-allowed disabled:opacity-60",
                action.color,
            )}
        >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 transition-colors group-hover:bg-white/10">
                <Icon className="h-5 w-5 text-zinc-200" />
            </div>
            <div className="w-full">
                <div className="line-clamp-2 min-h-[3rem] text-base font-medium text-zinc-100">
                    {t(action.labelKey)}
                </div>
                <div className="mt-1 line-clamp-2 min-h-[2.875rem] text-sm leading-relaxed text-zinc-500">
                    {t(action.descKey)}
                </div>
            </div>
        </button>
    );
});
